import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import { Form, Select, Button, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getUsers } from "../features/cutomers/customerSlice";
import { getCollectionInfo } from "../features/invoice/invoiceSlice";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

Chart.register(...registerables);

const CollectionStatus = () => {
  const chartInstanceRef = useRef(null);
  const [vcData, setVcData] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedVC, setSelectedVC] = useState("");
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [chartInstance, setChartInstance] = useState(null);
  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );
  const vclistState = useSelector((state) => state.customer.customers || []);
  const { collectionInfo = { states: [] }, isLoading } = useSelector(
    (state) => state.invoice || {}
  );
  console.log(collectionInfo);

  // Mock data for testing (uncomment to test if API is the issue)
  useEffect(() => {
    dispatch(getStates());
    dispatch(getUsers());
    dispatch(getCollectionInfo())
      .then((response) => {
        const data = response.payload?.states || [];
        console.log("API Response (Initial Collection Info):", data);
        const filteredData = data
          .filter((item) => item.state !== null)
          .map((item) => ({
            ...item,
            state: item.state || "Unknown",
          }));
        console.log("Processed filteredTableData:", filteredData);
        setFilteredTableData(filteredData);
        // Uncomment below to test with mock data if API only returns UP
        // setFilteredTableData(mockData);
        if (filteredData.length > 0) {
          renderChart(filteredData);
        } else {
          message.warning("No collection data available.");
        }
      })
      .catch((error) => {
        console.error("Error fetching initial data:", error);
        message.error("Failed to load initial data.");
      });
  }, [dispatch]);

  useEffect(() => {
    if (filteredTableData.length > 0 && chartInstanceRef.current) {
      console.log("Re-rendering chart with:", filteredTableData);
      renderChart(filteredTableData);
    }
  }, [filteredTableData]);

  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedVC("");
    setVcData([]);
    if (value) {
      const filteredVC = vclistState.filter(
        (customer) => customer.state === value
      );
      setVcData(filteredVC);
    }
  };

  const handleVcChange = (value) => {
    setSelectedVC(value);
  };

  const handleClear = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedVC("");
    setVcData([]);
    dispatch(getCollectionInfo()).then((response) => {
      const data = response.payload?.states || [];
      const filteredData = data
        .filter((item) => item.state !== null)
        .map((item) => ({
          ...item,
          state: item.state || "Unknown",
        }));
      setFilteredTableData(filteredData);
    });
  };

  const handleSubmit = () => {
    const filters = {};
    if (selectedState) filters.state = selectedState;
    if (selectedVC) filters.vc = selectedVC;

    dispatch(getCollectionInfo(filters))
      .then((response) => {
        const data = response.payload?.states || [];
        let filteredData = data
          .filter((item) => item.state !== null)
          .map((item) => ({
            ...item,
            state: item.state || "Unknown",
          }));

        if (filters.state) {
          filteredData = filteredData.filter(
            (item) => item.state === filters.state
          );
        }
        if (filters.vc) {
          filteredData = filteredData.filter(
            (item) =>
              item.vcUser_id?._id === filters.vc || item.vc === filters.vc
          );
        }

        setFilteredTableData(filteredData);
        if (filteredData.length === 0) {
          message.info("No data matches the selected filters.");
        }
      })
      .catch((error) => {
        console.error("Filter Error:", error);
        message.error("Failed to apply filters.");
      });
  };

  const renderChart = (data) => {
    const ctx = chartInstanceRef.current?.getContext("2d");
    if (!ctx) {
      console.log("Chart context not available");
      return;
    }

    if (chartInstance) {
      chartInstance.destroy();
    }

    const labels = data.map((item) => item.state);
    const beginningOutstanding = data.map(
      (item) => item.totalInvoiceLastMonth || 0
    );
    const billing = data.map((item) => item.totalAmountRaised || 0);
    const collection = data.map((item) => item.totalReceived || 0);
    const netOnDate = data.map((item) => item.netPayable || 0);

    console.log("Chart Data Being Rendered:", {
      labels,
      beginningOutstanding,
      billing,
      collection,
      netOnDate,
    });

    const newChartInstance = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Beginning Outstanding",
            data: beginningOutstanding,
            backgroundColor: "red",
            hoverBackgroundColor: "darkred",
            borderWidth: 1,
          },
          {
            label: "+Billing",
            data: billing,
            backgroundColor: "orange",
            hoverBackgroundColor: "darkorange",
            borderWidth: 1,
          },
          {
            label: "Collection",
            data: collection,
            backgroundColor: "green",
            hoverBackgroundColor: "darkgreen",
            borderWidth: 1,
          },
          {
            label: "Net-On-Date",
            data: netOnDate,
            backgroundColor: "maroon",
            hoverBackgroundColor: "darkmaroon",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          tooltip: {
            callbacks: {
              label: (tooltipItem) =>
                `${
                  tooltipItem.dataset.label
                }: ₹${tooltipItem.raw.toLocaleString()}`,
            },
          },
          legend: { position: "top" },
        },
        scales: {
          x: {
            title: { display: true, text: "States" },
            ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 }, // Show all labels, rotate if needed
          },
          y: {
            title: { display: true, text: "Amount (₹)" },
            ticks: { callback: (value) => `₹${value.toLocaleString()}` },
          },
        },
      },
    });

    setChartInstance(newChartInstance);
  };

  const styles = {
    th: { padding: "10px", textAlign: "left", backgroundColor: "#f4f4f4" },
    td: { padding: "10px" },
    tableContainer: { maxHeight: "500px", overflowY: "auto" },
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4 style={{ fontSize: "large", fontWeight: "bold" }}>
          Status on Collection
        </h4>
        <p style={{ fontSize: "0.875rem", color: "rgb(107, 114, 128)" }}>
          Last Updated: {new Date().toLocaleTimeString()}
        </p>
      </div>
      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginTop: "20px" }}
      >
        <Form.Item name="state_name">
          <Select
            style={{ width: 160, marginRight: 16 }}
            placeholder="Select State"
            onChange={handleStateChange}
            value={selectedState}
            allowClear
          >
            {statelistState.map((i, j) => (
              <Select.Option key={j} value={i.state}>
                {i.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {/* <Form.Item name="vc_name">
          <Select
            style={{ width: 160, marginRight: 16 }}
            placeholder="Select VC"
            onChange={handleVcChange}
            value={selectedVC}
            allowClear
            disabled={!selectedState}
          >
            {vcData
              .filter((item) => item.role === "vc")
              .map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item> */}
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Apply Filter
          </Button>
        </Form.Item>
        <Form.Item>
          <Button onClick={handleClear}>Reset Filter</Button>
        </Form.Item>
      </Form>
      <div
        style={{
          backgroundColor: "white",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "0.5rem",
          padding: "1rem",
          marginTop: "0.5rem",
          height: "auto",
          width: "auto",
        }}
      >
        {isLoading ? (
          <p>Loading...</p>
        ) : filteredTableData.length > 0 ? (
          <canvas ref={chartInstanceRef} height="300" width="600"></canvas>
        ) : (
          <p>No Data Available</p>
        )}
      </div>
      <div
        className="table-container"
        style={{ marginTop: "20px", ...styles.tableContainer }}
      >
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={styles.th}>State</th>
              <th style={styles.th}>Beginning Outstanding</th>
              <th style={styles.th}>+Billing</th>
              <th style={styles.th}>Collection</th>
              <th style={styles.th}>Net-On-Date</th>
              <th style={styles.th}>%</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="6">Loading...</td>
              </tr>
            ) : filteredTableData.length > 0 ? (
              filteredTableData.map((item, index) => (
                <tr key={index}>
                  <td style={styles.td}>{item.state || "Unknown"}</td>
                  <td style={styles.td}>
                    ₹{(item.totalInvoiceLastMonth || 0).toLocaleString()}
                  </td>
                  <td style={styles.td}>
                    ₹{(item.totalAmountRaised || 0).toLocaleString()}
                  </td>
                  <td style={styles.td}>
                    ₹{(item.totalReceived || 0).toLocaleString()}
                  </td>
                  <td style={styles.td}>
                    ₹{(item.netPayable || 0).toLocaleString()}
                  </td>
                  <td style={styles.td}>{item.percentageCollected || "N/A"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">No Data Available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CollectionStatus;
